import { cn } from '@/lib/cn';
import type { ApiKeyUsageItem } from '@innermate/shared';

interface Props {
  items: ApiKeyUsageItem[];
  /** 사용자 컬럼 표시 여부 (관리자 페이지=true, 사용자 페이지=false). */
  showUser?: boolean;
}

function formatTokens(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', { hour12: false });
}

function statusTone(code: number): 'ok' | 'warn' | 'err' {
  if (code >= 200 && code < 300) return 'ok';
  if (code >= 300 && code < 500) return 'warn';
  return 'err';
}

export function UsageTable({ items, showUser = true }: Props) {
  return (
    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th>시각</Th>
            {showUser ? <Th>사용자</Th> : null}
            <Th>키</Th>
            <Th>경로</Th>
            <Th>모델</Th>
            <Th align="right">토큰 (입력 / 출력 / 합계)</Th>
            <Th align="right">소요</Th>
            <Th align="right">상태</Th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={showUser ? 8 : 7} className="text-center text-foreground-muted py-10">
                사용량 기록이 없습니다.
              </td>
            </tr>
          ) : (
            items.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-surface-muted/30">
                <td className="px-4 py-2.5 whitespace-nowrap text-xs">{formatTime(r.createdAt)}</td>
                {showUser ? <td className="px-4 py-2.5 text-xs">{r.userEmail ?? '—'}</td> : null}
                <td className="px-4 py-2.5 text-xs">
                  <div className="font-medium">{r.keyName ?? '(삭제됨)'}</div>
                  {r.keyPrefix ? (
                    <div className="font-mono text-[10px] text-foreground-muted">
                      {r.keyPrefix}…
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{r.endpointPath}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{r.model ?? '—'}</td>
                <td className="px-4 py-2.5 text-right text-xs font-mono whitespace-nowrap">
                  {formatTokens(r.promptTokens)} / {formatTokens(r.completionTokens)} /{' '}
                  {formatTokens(r.totalTokens)}
                </td>
                <td className="px-4 py-2.5 text-right text-xs font-mono whitespace-nowrap">
                  {formatDuration(r.durationMs)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <StatusBadge code={r.statusCode} error={r.error} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function StatusBadge({ code, error }: { code: number; error: string | null }) {
  const tone = statusTone(code);
  const cls = {
    ok: 'bg-success/15 text-success',
    warn: 'bg-amber-500/15 text-amber-600',
    err: 'bg-destructive/15 text-destructive',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider font-mono',
        cls,
      )}
      title={error ?? undefined}
    >
      {code}
    </span>
  );
}
